import React from "react";
import Tooltip from "@mui/material/Tooltip";

const toIdSet = (ids) => {
  if (!ids) {
    return null;
  }
  return new Set(
    Array.from(ids)
      .filter((id) => id != null)
      .map((id) => String(id)),
  );
};

export const getAccertamentiVisitaRowId = (row) => row?._rowId ?? row?.ID;

export const applyAccertamentoVisitaEdit = (row, edited, scadenza) => ({
  ...row,
  Scadenza: scadenza,
  // I prezzi della riga categoria sono totali, non importi dei singoli esami.
  ...(!edited._isCategoriaGroup && {
    Importo: edited.Importo,
    ImportoPassivo: edited.ImportoPassivo,
  }),
});

export const renderAccertamentoVisitaCell = (params) => {
  const value = params.value || "";
  const tooltip = params.row?._accertamentiTooltip;

  if (!tooltip) {
    return value;
  }

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <span
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
};

export const buildAccertamentiVisitaRows = (
  rows,
  selectedIds,
  selectedRows = [],
) => {
  const selectedIdSet = toIdSet(selectedIds);
  const selectedPackageIdSet = new Set(
    (selectedRows || [])
      .filter((row) => Number(row?.Pacchetto) === 1)
      .map((row) => String(row.ID)),
  );
  const packageCategories = new Set(
    (selectedRows || [])
      .filter((row) => Number(row?.Pacchetto) === 1 && row?.Categoria)
      .map((row) => String(row.Categoria)),
  );
  const isSelected = (id) =>
    selectedIdSet ? selectedIdSet.has(String(id)) : true;
  const gruppi = new Map();
  const risultato = [];

  (rows || []).forEach((row) => {
    const selezionato = isSelected(row.ID);
    const importoContributo = selezionato ? Number(row.Importo) || 0 : 0;
    const importoPassivoContributo = selezionato
      ? Number(row.ImportoPassivo) || 0
      : 0;

    const categoriaConPacchettoSelezionato =
      row.Categoria && packageCategories.has(String(row.Categoria));
    const appartieneAlGruppo =
      row.Categoria &&
      (!categoriaConPacchettoSelezionato ||
        selectedPackageIdSet.has(String(row.ID)));

    if (appartieneAlGruppo) {
      const key = String(row.Categoria);
      if (!gruppi.has(key)) {
        const gruppo = {
          ...row,
          Categoria: row.Categoria,
          Importo: importoContributo,
          ImportoPassivo: importoPassivoContributo,
          _rowId: `categoria:${key}`,
          _ids: [row.ID],
          _idsSelezionati: selezionato ? [row.ID] : [],
          _descrizioni: row.Descrizione ? [row.Descrizione] : [],
          _isCategoriaGroup: true,
        };
        gruppi.set(key, gruppo);
        risultato.push(gruppo);
      } else {
        const gruppo = gruppi.get(key);
        gruppo._ids.push(row.ID);
        if (selezionato) {
          gruppo._idsSelezionati.push(row.ID);
        }
        if (row.Descrizione) {
          gruppo._descrizioni.push(row.Descrizione);
        }
        gruppo.Importo += importoContributo;
        gruppo.ImportoPassivo += importoPassivoContributo;
        if (!gruppo.Scadenza && row.Scadenza) {
          gruppo.Scadenza = row.Scadenza;
        }
      }
    }

    risultato.push({
      ...row,
      _rowId: `accertamento:${row.ID}`,
      _ids: [row.ID],
      _idsSelezionati: selezionato ? [row.ID] : [],
      _isCategoriaGroup: false,
    });
  });

  risultato.forEach((row) => {
    if (!row._isCategoriaGroup) {
      return;
    }
    row.Importo = Math.round((row.Importo || 0) * 100) / 100;
    row.ImportoPassivo = Math.round((row.ImportoPassivo || 0) * 100) / 100;
    const nSelezionati = row._idsSelezionati?.length || 0;
    row.Descrizione =
      nSelezionati > 0
        ? `Pacchetto: ${row.Categoria} (${nSelezionati})`
        : `Pacchetto: ${row.Categoria}`;
    row._accertamentiTooltip = row._descrizioni?.join(", ") || "";
  });

  const categorieCompletamenteSelezionate = new Set(
    risultato
      .filter((row) => {
        if (!row._isCategoriaGroup || !selectedIdSet) {
          return false;
        }

        const nSelezionati = row._idsSelezionati?.length || 0;
        const totaleAccertamenti = row._ids?.length || 0;

        return (
          totaleAccertamenti > 1 &&
          nSelezionati === totaleAccertamenti &&
          row._ids.every((id) => selectedPackageIdSet.has(String(id)))
        );
      })
      .map((row) => String(row.Categoria)),
  );

  return risultato.filter((row) => {
    if (
      !row._isCategoriaGroup &&
      selectedIdSet &&
      row.Categoria &&
      categorieCompletamenteSelezionate.has(String(row.Categoria)) &&
      selectedPackageIdSet.has(String(row.ID))
    ) {
      return false;
    }

    if (!row._isCategoriaGroup || !selectedIdSet) {
      return true;
    }

    const nSelezionati = row._idsSelezionati?.length || 0;
    const totaleAccertamenti = row._ids?.length || 0;

    return (
      nSelezionati === 0 ||
      (totaleAccertamenti > 1 &&
        nSelezionati === totaleAccertamenti &&
        row._ids.every((id) => selectedPackageIdSet.has(String(id))))
    );
  });
};

export const getAccertamentiVisitaSelectionRowIds = (rows, selectedIds) => {
  const selectedIdSet = toIdSet(selectedIds) || new Set();

  return (rows || [])
    .filter((row) => {
      const ids = row._ids || [];
      if (row._isCategoriaGroup) {
        return (
          ids.length > 0 && ids.every((id) => selectedIdSet.has(String(id)))
        );
      }
      return ids.some((id) => selectedIdSet.has(String(id)));
    })
    .map(getAccertamentiVisitaRowId);
};

export const getAccertamentiVisitaSelectionChanges = (
  rows,
  nextRowIds,
  previousRowIds,
) => {
  const newRepIds = new Set((nextRowIds || []).map(String));
  const oldRepIds = new Set((previousRowIds || []).map(String));
  const righe = rows || [];
  const categorieConFigliSelezionati = new Set(
    righe
      .filter(
        (row) =>
          !row._isCategoriaGroup &&
          row.Categoria &&
          newRepIds.has(String(getAccertamentiVisitaRowId(row))),
      )
      .map((row) => String(row.Categoria)),
  );

  const aggiunti = righe.filter((row) => {
    const rowId = String(getAccertamentiVisitaRowId(row));
    if (!newRepIds.has(rowId) || oldRepIds.has(rowId)) {
      return false;
    }

    return !(
      row._isCategoriaGroup &&
      categorieConFigliSelezionati.has(String(row.Categoria))
    );
  });

  const rimossi = righe.filter((row) => {
    const rowId = String(getAccertamentiVisitaRowId(row));
    return oldRepIds.has(rowId) && !newRepIds.has(rowId);
  });

  return { aggiunti, rimossi };
};

export const getAccertamentiVisitaSelectedRows = (
  currentSelection,
  allRows,
  addedRows,
  removedRows,
) => {
  const idsAggiunti = new Set(
    (addedRows || []).flatMap((r) => r._ids).map(String),
  );
  const idsRimossi = new Set(
    (removedRows || []).flatMap((r) => r._ids).map(String),
  );
  const idsAggiuntiDaPacchetto = new Set(
    (addedRows || [])
      .filter((row) => row._isCategoriaGroup)
      .flatMap((row) => row._ids)
      .map(String),
  );

  const invariati = (currentSelection || []).filter((sel) => {
    const id = String(sel.ID);
    return !idsRimossi.has(id) && !idsAggiuntiDaPacchetto.has(id);
  });
  const idsGiaPresenti = new Set(invariati.map((sel) => String(sel.ID)));
  const nuovi = (allRows || [])
    .filter((row) => {
      const id = String(row.ID);
      return idsAggiunti.has(id) && !idsGiaPresenti.has(id);
    })
    .map((row) => {
      row.Pacchetto = idsAggiuntiDaPacchetto.has(String(row.ID)) ? 1 : 0;
      return row;
    });

  return {
    idsAggiunti,
    idsRimossi,
    idsAggiuntiDaPacchetto,
    invariati,
    nuovi,
    selectedRows: [...invariati, ...nuovi],
  };
};

export const markProtocolloSanitarioPacchetti = (
  selectedRows,
  allRows,
  protocolli = selectedRows,
) => {
  const protocolliById = new Map(
    (protocolli || [])
      .filter((row) => row?.Accertamenti != null || row?.ID != null)
      .map((row) => [String(row.Accertamenti ?? row.ID), row]),
  );
  return (selectedRows || []).map((row) => ({
    ...row,
    Pacchetto: Number(
      protocolliById.get(String(row.ID))?.Pacchetto ?? row?.Pacchetto ?? 0,
    ),
  }));
};

export const getAccertamentiVisitaBackupSelection = (
  currentBackup,
  selectedRows,
  protocolli,
  tipologiaVisita,
) => {
  const selected = Array.isArray(selectedRows) ? selectedRows : [];
  if (String(tipologiaVisita) === "2") {
    return selected;
  }

  const selectedIds = new Set(selected.map((row) => String(row.ID)));
  const protocolliPreventiva = new Set(
    (protocolli || [])
      .filter((protocollo) => Number(protocollo.Preventiva) === 1)
      .map((protocollo) => String(protocollo.Accertamenti)),
  );
  const hiddenPreventiva = (currentBackup || []).filter(
    (row) =>
      protocolliPreventiva.has(String(row.ID)) &&
      !selectedIds.has(String(row.ID)),
  );

  return [...hiddenPreventiva, ...selected];
};
